import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Collapse,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarTodayIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Notifications as NotificationsIcon,
  ArrowForward as ArrowForwardIcon,
  PersonAdd as PersonAddIcon,
  Assessment as AssessmentIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Receipt as ReceiptIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const DashboardHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeLoans: 0,
    pendingLoans: 0,
    overdueInstallments: 0,
    totalCustomers: 0,
    totalUsers: 0,
    totalAreas: 0,
    verifiedLoans: 0,
    rejectedLoans: 0,
    closedLoans: 0,
  });
  const [notificationCounts, setNotificationCounts] = useState({
    pendingRequests: 0,
    pendingLoans: 0,
    overdueInstallments: 0,
  });
  const [netProfit, setNetProfit] = useState(0);
  const [loadingProfit, setLoadingProfit] = useState(false);
  const [profitDialogOpen, setProfitDialogOpen] = useState(false);
  const [profitDetails, setProfitDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedLoans, setExpandedLoans] = useState(new Set());
  const [recentLoans, setRecentLoans] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [capitalData, setCapitalData] = useState(null);
  
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Due Today section state
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dueInstallments, setDueInstallments] = useState([]);
  const [loanBreakdowns, setLoanBreakdowns] = useState({});
  const [loadingDue, setLoadingDue] = useState(false);
  const [dueDialogOpen, setDueDialogOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [selectedBreakdown, setSelectedBreakdown] = useState(null);
  const [dueSummary, setDueSummary] = useState({ 
    totalPayable: 0, 
    count: 0, 
    totalDue: 0, 
    totalArrears: 0,
    totalCollected: 0,
    totalPending: 0,
    collectedCount: 0,
    pendingCount: 0,
    collectionRate: 0,
  });

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [startDate, endDate]);
  
  useEffect(() => {
    fetchDueInstallments();
  }, [dueDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [
        statsRes,
        notificationsRes,
        profitRes,
        loansRes,
        customersRes,
        requestsRes,
        usersRes,
        areasRes,
        capitalRes,
      ] = await Promise.allSettled([
        api.get('/dashboard/stats'),
        api.get('/admin/notifications/counts'),
        api.get('/admin/capital/profit', { params: { startDate, endDate } }),
        api.get('/admin/loans'),
        api.get('/admin/customers'),
        api.get('/admin/customer-requests/pending'),
        api.get('/admin/users'),
        api.get('/admin/areas'),
        api.get('/admin/capital/current'),
      ]);

      // Process stats
      const statsData = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
      const notificationsData = notificationsRes.status === 'fulfilled' ? notificationsRes.value.data : {};
      const profitData = profitRes.status === 'fulfilled' ? profitRes.value.data : { totalProfit: 0 };
      const loans = loansRes.status === 'fulfilled' ? loansRes.value.data : [];
      const customers = customersRes.status === 'fulfilled' ? customersRes.value.data : [];
      const requests = requestsRes.status === 'fulfilled' ? requestsRes.value.data : [];
      const users = usersRes.status === 'fulfilled' ? usersRes.value.data : [];
      const areas = areasRes.status === 'fulfilled' ? areasRes.value.data : [];
      const capital = capitalRes.status === 'fulfilled' ? capitalRes.value.data : null;

      // Calculate statistics
      const totalLoans = loans.length;
      const activeLoans = loans.filter(l => l.status === 'ACTIVE').length;
      const pendingLoans = loans.filter(l => l.status === 'PENDING').length;
      const verifiedLoans = loans.filter(l => l.status === 'VERIFIED').length;
      const rejectedLoans = loans.filter(l => l.status === 'REJECTED').length;
      const closedLoans = loans.filter(l => l.status === 'CLOSED').length;

      setStats({
        activeLoans: statsData.activeLoans || activeLoans,
        pendingLoans: statsData.pendingLoans || pendingLoans,
        overdueInstallments: statsData.overdueInstallments || 0,
        totalCustomers: customers.length,
        totalUsers: users.length,
        totalAreas: areas.length,
        verifiedLoans,
        rejectedLoans,
        closedLoans,
      });

      setNotificationCounts(notificationsData);
      setNetProfit(profitData.totalProfit || 0);
      setCapitalData(capital);

      // Get recent loans (last 5)
      const sortedLoans = [...loans]
        .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
        .slice(0, 5);
      setRecentLoans(sortedLoans);

      // Get recent customers (last 5)
      const sortedCustomers = [...customers]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
      setRecentCustomers(sortedCustomers);

      // Get recent requests (last 5)
      const sortedRequests = [...requests]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
      setRecentRequests(sortedRequests);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.showToast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchNetProfit = async () => {
    setLoadingProfit(true);
    try {
      const response = await api.get('/admin/capital/profit', {
        params: { startDate, endDate },
      });
      setNetProfit(response.data.totalProfit || 0);
    } catch (error) {
      console.error('Error fetching net profit:', error);
      setNetProfit(0);
    } finally {
      setLoadingProfit(false);
    }
  };

  const handleProfitClick = async () => {
    setProfitDialogOpen(true);
    setLoadingDetails(true);
    try {
      const response = await api.get('/admin/capital/profit', {
        params: { startDate, endDate },
      });
      setProfitDetails(response.data);
    } catch (error) {
      console.error('Error fetching profit details:', error);
      setProfitDetails(null);
      toast.showToast('Failed to load profit details', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const fetchDueInstallments = async () => {
    setLoadingDue(true);
    try {
      const response = await api.get('/admin/installments/due', {
        params: { date: dueDate },
      });
      const data = response.data || {};
      const installments = data.installments || [];
      const breakdowns = data.loanBreakdowns || {};
      
      setDueInstallments(installments);
      setLoanBreakdowns(breakdowns);
      
      // Calculate total payable from all loan breakdowns
      let totalPayable = 0;
      Object.values(breakdowns).forEach((breakdown) => {
        if (breakdown && breakdown.totalPayable) {
          totalPayable += Number(breakdown.totalPayable) || 0;
        }
      });
      
      // Calculate collected and pending amounts
      let totalCollected = 0;
      let totalPending = 0;
      let collectedCount = 0;
      let pendingCount = 0;
      
      installments.forEach((inst) => {
        const breakdown = breakdowns[inst.loanId];
        const payable = breakdown?.totalPayable || inst.amountDue || 0;
        const paidAmount = inst.paidAmount || 0;
        
        if (inst.status === 'PAID' || paidAmount > 0) {
          totalCollected += Number(payable);
          collectedCount++;
        } else {
          totalPending += Number(payable);
          pendingCount++;
        }
      });
      
      const collectionRate = totalPayable > 0 ? (totalCollected / totalPayable) * 100 : 0;
      
      setDueSummary({
        totalPayable: totalPayable || data.totalDue || 0,
        count: data.count || installments.length,
        totalDue: data.totalDue || 0,
        totalArrears: data.totalArrears || 0,
        totalCollected,
        totalPending,
        collectedCount,
        pendingCount,
        collectionRate,
      });
    } catch (error) {
      console.error('Error fetching due installments:', error);
      setDueInstallments([]);
      setLoanBreakdowns({});
      setDueSummary({ 
        totalPayable: 0, 
        count: 0, 
        totalDue: 0, 
        totalArrears: 0,
        totalCollected: 0,
        totalPending: 0,
        collectedCount: 0,
        pendingCount: 0,
        collectionRate: 0,
      });
    } finally {
      setLoadingDue(false);
    }
  };
  
  const handleInstallmentClick = (installment) => {
    setSelectedInstallment(installment);
    setSelectedBreakdown(loanBreakdowns[installment.loanId] || null);
    setDueDialogOpen(true);
  };

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Format currency with exact decimal places (for profit breakdown to match Installment-wise Breakdown)
  const formatCurrencyExact = (val) => {
    if (!val && val !== 0) return 'Rs 0.00';
    const num = Number(val);
    return `Rs ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-LK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const toggleLoanExpansion = (loanId) => {
    const newExpanded = new Set(expandedLoans);
    if (newExpanded.has(loanId)) {
      newExpanded.delete(loanId);
    } else {
      newExpanded.add(loanId);
    }
    setExpandedLoans(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'APPROVED':
        return 'info';
      case 'PENDING':
      case 'VERIFIED':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'CLOSED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'APPROVED':
        return 'Approved';
      case 'PENDING':
        return 'Pending';
      case 'VERIFIED':
        return 'Verified';
      case 'REJECTED':
        return 'Rejected';
      case 'CLOSED':
        return 'Closed';
      default:
        return status;
    }
  };

  const StatCard = ({ title, value, icon, color, gradient, onClick, subtitle }) => (
    <Card
      sx={{
          background: gradient || `linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)`,
        color: 'white',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {value}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const totalPending = stats.pendingLoans + stats.verifiedLoans;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#0D47A1',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
          }}
        >
          Welcome back, {user?.fullName || 'Administrator'}!
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Complete overview of your loan management system
        </Typography>
      </Box>

      {/* Date Filters for Profit */}
      <Paper
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: 2,
          background: '#FFFFFF',
          border: '2px solid #0D47A1',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <CalendarTodayIcon sx={{ color: '#0D47A1', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1', flex: 1 }}>
            Date Range Filter
          </Typography>
          <TextField
            label="From Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 150 } }}
          />
          <TextField
            label="To Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 150 } }}
          />
          <Button
            variant="contained"
            onClick={fetchNetProfit}
            startIcon={<CalendarTodayIcon />}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
              },
            }}
          >
            Apply Filter
          </Button>
        </Box>
      </Paper>

      {/* Primary Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Loans"
            value={stats.activeLoans}
            icon={<AccountBalanceIcon sx={{ fontSize: 32 }} />}
            gradient="linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)"
            onClick={() => navigate('/admin/customer-loan-schedule')}
            subtitle="Currently active"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Approval"
            value={totalPending}
            icon={<PendingIcon sx={{ fontSize: 32 }} />}
            gradient="linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
            onClick={() => navigate('/admin/loan-approval')}
            subtitle={`${stats.pendingLoans} pending, ${stats.verifiedLoans} verified`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue Installments"
            value={stats.overdueInstallments}
            icon={<WarningIcon sx={{ fontSize: 32 }} />}
            gradient="linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)"
            onClick={() => navigate('/admin/late-payments')}
            subtitle="Requires attention"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Net Profit"
            value={loadingProfit ? '...' : formatCurrency(netProfit)}
            icon={<AttachMoneyIcon sx={{ fontSize: 32 }} />}
            gradient={netProfit >= 0 
              ? 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)'
              : 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)'
            }
            onClick={handleProfitClick}
            subtitle="Click to view details"
          />
        </Grid>
      </Grid>

      {/* Secondary Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.totalCustomers}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Customers
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <PersonAddIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.totalUsers}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              System Users
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <LocationOnIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.totalAreas}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Active Areas
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.closedLoans}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Closed Loans
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Capital Overview */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: '#FFFFFF',
          border: '2px solid #0D47A1',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#0D47A1' }}>
          Current Capital
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {formatCurrency(capitalData?.currentCapital || capitalData?.totalCapital || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Available Capital
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCurrency(capitalData?.totalIncome || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Income
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                {formatCurrency(capitalData?.totalExpenses || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Expenses
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Due Today Section */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1', mb: 0.5 }}>
              Due Today
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Installments due for collection
            </Typography>
          </Box>
          <TextField
            label="Select Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 200 }}
          />
        </Box>

        {/* Collection Report Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(13, 71, 161, 0.2)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {loadingDue ? '...' : formatCurrency(dueSummary.totalPayable)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Payable
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                      {dueSummary.count} installment(s)
                    </Typography>
                  </Box>
                  <AttachMoneyIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(46, 125, 50, 0.2)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {loadingDue ? '...' : formatCurrency(dueSummary.totalCollected)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Collected Amount
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                      {dueSummary.collectedCount} paid installment(s)
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(211, 47, 47, 0.2)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {loadingDue ? '...' : formatCurrency(dueSummary.totalPending)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Pending Amount
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                      {dueSummary.pendingCount} unpaid installment(s)
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(255, 152, 0, 0.2)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {loadingDue ? '...' : `${dueSummary.collectionRate.toFixed(1)}%`}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Collection Rate
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                      {dueSummary.collectedCount}/{dueSummary.count} collected
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Collection Progress Bar */}
        {!loadingDue && dueSummary.totalPayable > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Collection Progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {dueSummary.collectionRate.toFixed(1)}% Complete
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={dueSummary.collectionRate} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: dueSummary.collectionRate >= 80 ? '#2E7D32' : 
                                   dueSummary.collectionRate >= 50 ? '#FF9800' : '#D32F2F',
                  borderRadius: 5,
                }
              }} 
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                Collected: {formatCurrency(dueSummary.totalCollected)}
              </Typography>
              <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                Pending: {formatCurrency(dueSummary.totalPending)}
              </Typography>
            </Box>
          </Box>
        )}

        {loadingDue ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : dueInstallments.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <CalendarTodayIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No installments due for {new Date(dueDate).toLocaleDateString()}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Mobile Card View */}
            {isMobile && (
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Stack spacing={2}>
                  {dueInstallments.map((inst) => {
                    const breakdown = loanBreakdowns[inst.loanId];
                    const totalPayable = breakdown?.totalPayable || inst.totalPayable || inst.amountDue;
                    
                    return (
                      <Card
                        key={inst.installmentId}
                        onClick={() => handleInstallmentClick(inst)}
                        sx={{
                          borderRadius: 2,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(13, 71, 161, 0.2)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                Customer
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                {inst.customerName}
                              </Typography>
                            </Box>
                            <Chip
                              label={inst.status}
                              size="small"
                              color={
                                inst.status === 'PAID' ? 'success' :
                                inst.status === 'OVERDUE' || inst.status === 'LATE' ? 'error' :
                                inst.status === 'PARTIAL' ? 'warning' : 'default'
                              }
                              sx={{ fontSize: '0.65rem', height: 24 }}
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                Loan ID
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                #{inst.loanId}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                Installment #
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                {inst.installmentNumber}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                Due Date
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                {new Date(inst.dueDate).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                              Base Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                              {formatCurrency(inst.baseAmount || 0)}
                            </Typography>
                          </Box>

                          {inst.lateInterest > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                                Late Interest
                              </Typography>
                              <Chip
                                label={formatCurrency(inst.lateInterest)}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Box>
                          )}

                          <Box sx={{ mb: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                              Total Payable
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0D47A1' }}>
                              {formatCurrency(totalPayable)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                Paid Amount
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  color: (inst.paidAmount || 0) > 0 ? 'success.main' : 'text.secondary'
                                }}
                              >
                                {formatCurrency(inst.paidAmount || 0)}
                              </Typography>
                            </Box>
                            <Box>
                              {inst.status === 'PAID' || (inst.paidAmount || 0) > 0 ? (
                                <Chip
                                  label="Collected"
                                  size="small"
                                  color="success"
                                  icon={<CheckCircleIcon sx={{ fontSize: '0.875rem !important' }} />}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ) : (
                                <Chip
                                  label="Pending"
                                  size="small"
                                  color="error"
                                  icon={<PendingIcon sx={{ fontSize: '0.875rem !important' }} />}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Desktop/Tablet Table View */}
            <TableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Loan ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Installment #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Base Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Late Interest</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Payable</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Paid Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Collection</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dueInstallments.map((inst) => {
                    const breakdown = loanBreakdowns[inst.loanId];
                    const totalPayable = breakdown?.totalPayable || inst.totalPayable || inst.amountDue;
                    
                    return (
                      <TableRow
                        key={inst.installmentId}
                        hover
                        onClick={() => handleInstallmentClick(inst)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{inst.customerName}</TableCell>
                        <TableCell>#{inst.loanId}</TableCell>
                        <TableCell>{inst.installmentNumber}</TableCell>
                        <TableCell>{new Date(inst.dueDate).toLocaleDateString()}</TableCell>
                        <TableCell>{formatCurrency(inst.baseAmount || 0)}</TableCell>
                        <TableCell>
                          {inst.lateInterest > 0 ? (
                            <Chip
                              label={formatCurrency(inst.lateInterest)}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {formatCurrency(0)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatCurrency(totalPayable)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: (inst.paidAmount || 0) > 0 ? 'success.main' : 'text.secondary'
                            }}
                          >
                            {formatCurrency(inst.paidAmount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={inst.status}
                            size="small"
                            color={
                              inst.status === 'PAID' ? 'success' :
                              inst.status === 'OVERDUE' || inst.status === 'LATE' ? 'error' :
                              inst.status === 'PARTIAL' ? 'warning' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {inst.status === 'PAID' || (inst.paidAmount || 0) > 0 ? (
                            <Chip
                              label="Collected"
                              size="small"
                              color="success"
                              icon={<CheckCircleIcon />}
                            />
                          ) : (
                            <Chip
                              label="Pending"
                              size="small"
                              color="error"
                              icon={<PendingIcon />}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Quick Actions */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: '#FFFFFF',
          border: '2px solid #0D47A1',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#0D47A1' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => navigate('/admin/loan-approval')}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                },
              }}
            >
              Approve Loans
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate('/admin/customer-requests')}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%)',
                },
              }}
            >
              Review Requests
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AccountBalanceWalletIcon />}
              onClick={() => navigate('/admin/capital-overview')}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1B5E20 0%, #0D3E0F 100%)',
                },
              }}
            >
              Capital Overview
            </Button>
          </Grid>
        </Grid>
      </Paper>


      {/* Recent Activity */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Recent Loans */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                Recent Loans
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/loan-management')}
              >
                View All
              </Button>
            </Box>
            {recentLoans.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  No loans yet
                </Typography>
              </Box>
            ) : (
              <List>
                {recentLoans.map((loan, index) => (
                  <React.Fragment key={loan.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: theme.palette[getStatusColor(loan.status)]?.main || 'grey.500',
                          }}
                        >
                          <DescriptionIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {loan.customer?.fullName || 'Unknown'}
                            </Typography>
                            <Chip
                              label={getStatusLabel(loan.status)}
                              color={getStatusColor(loan.status)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="textSecondary">
                            {formatCurrency(loan.principalAmount || 0)} • {formatDate(loan.updatedAt || loan.createdAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < recentLoans.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Recent Customers */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                Recent Customers
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/customers')}
              >
                View All
              </Button>
            </Box>
            {recentCustomers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  No customers yet
                </Typography>
              </Box>
            ) : (
              <List>
                {recentCustomers.map((customer, index) => (
                  <React.Fragment key={customer.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <PeopleIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {customer.fullName}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              NIC: {customer.nic} • {formatDate(customer.createdAt)}
                            </Typography>
                            {customer.area && (
                              <Typography variant="caption" color="textSecondary">
                                Area: {customer.area.name}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < recentCustomers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Recent Requests */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                Pending Requests
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/admin/customer-requests')}
              >
                View All
              </Button>
            </Box>
            {recentRequests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  No pending requests
                </Typography>
              </Box>
            ) : (
              <List>
                {recentRequests.map((request, index) => (
                  <React.Fragment key={request.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <AssignmentIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {request.customer?.fullName || 'Unknown'}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="textSecondary">
                            {request.requestType} • {formatDate(request.createdAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < recentRequests.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Alerts */}
      {(notificationCounts.pendingLoans > 0 || notificationCounts.pendingRequests > 0 || notificationCounts.overdueInstallments > 0) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {notificationCounts.pendingLoans > 0 && (
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                  border: '1px solid',
                  borderColor: 'warning.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PendingIcon sx={{ color: 'warning.main', fontSize: 32 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {notificationCounts.pendingLoans} Loan{notificationCounts.pendingLoans > 1 ? 's' : ''} Pending
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Awaiting your approval
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => navigate('/admin/loan-approval')}
                  >
                    Review
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}
          {notificationCounts.pendingRequests > 0 && (
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                  border: '1px solid',
                  borderColor: 'info.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssignmentIcon sx={{ color: 'info.main', fontSize: 32 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {notificationCounts.pendingRequests} Request{notificationCounts.pendingRequests > 1 ? 's' : ''} Pending
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Customer update requests
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={() => navigate('/admin/customer-requests')}
                  >
                    Review
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}
          {notificationCounts.overdueInstallments > 0 && (
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                  border: '1px solid',
                  borderColor: 'error.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <WarningIcon sx={{ color: 'error.main', fontSize: 32 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {notificationCounts.overdueInstallments} Overdue
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Installments require attention
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => navigate('/admin/late-payments')}
                  >
                    View
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Profit Details Dialog */}
      <Dialog
        open={profitDialogOpen}
        onClose={() => setProfitDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0D47A1' }}>
                Net Profit Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
              </Typography>
            </Box>
            <IconButton onClick={() => setProfitDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : profitDetails ? (
            <Box>
              {/* Summary Card */}
              <Card
                sx={{
                  mb: 3,
                  background: profitDetails.totalProfit >= 0
                    ? 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)'
                    : 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                  color: 'white',
                }}
              >
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Total Profit
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {formatCurrencyExact(profitDetails.totalProfit)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Paid Installments
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {profitDetails.paidInstallmentCount || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Total Interest
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {formatCurrencyExact((profitDetails.installmentInterestProfit || 0) + (profitDetails.lateInterestProfit || 0))}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Total Expenses
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: profitDetails.totalExpenses > 0 ? '#ff9800' : 'inherit' }}>
                        {formatCurrencyExact(profitDetails.totalExpenses || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Active Loans
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {profitDetails.loanDetails?.length || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Loan Details */}
              {profitDetails.loanDetails && profitDetails.loanDetails.length > 0 ? (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                    Profit by Loan
                  </Typography>
                  {profitDetails.loanDetails.map((loan, index) => {
                    const loanId = loan.loanId;
                    const isExpanded = expandedLoans.has(loanId);
                    const profitFromThisLoan = (loan.profitPerInstallment || 0) * (loan.paidCount || 0);
                    const interestFromThisLoan = loan.totalInterest || 0;
                    const totalProfitFromThisLoan = profitFromThisLoan + interestFromThisLoan;

                    return (
                      <Card key={loanId || index} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleLoanExpansion(loanId)}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Loan #{loanId}
                                </Typography>
                                {loan.customerName && (
                                  <Chip
                                    label={loan.customerName}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={`Total Profit: ${formatCurrencyExact(totalProfitFromThisLoan)}`}
                                  color={totalProfitFromThisLoan >= 0 ? 'success' : 'error'}
                                  size="small"
                                />
                                {interestFromThisLoan > 0 && (
                                  <Chip
                                    label={`Interest: ${formatCurrencyExact(interestFromThisLoan)}`}
                                    color="warning"
                                    size="small"
                                  />
                                )}
                                <Chip
                                  label={`Paid: ${loan.paidCount || 0}/${loan.remainingInstallments || 0}`}
                                  color="primary"
                                  size="small"
                                />
                                {loan.templateName && (
                                  <Chip
                                    label={loan.templateName}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </Box>
                            <IconButton>
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>

                          <Collapse in={isExpanded}>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              {loan.customerName && (
                                <Grid item xs={12}>
                                  <Typography variant="body2" color="text.secondary">
                                    Customer
                                  </Typography>
                                  <Typography variant="h6">
                                    {loan.customerName} {loan.customerNIC && `(${loan.customerNIC})`}
                                  </Typography>
                                </Grid>
                              )}
                              {loan.approvedAmount && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Approved Loan Amount
                                  </Typography>
                                  <Typography variant="h6">
                                    {formatCurrencyExact(loan.approvedAmount)}
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Principal (Bank Deposit)
                                </Typography>
                                <Typography variant="h6" color="error">
                                  {formatCurrencyExact(loan.principal)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Remaining Installments
                                </Typography>
                                <Typography variant="h6" color="info.main">
                                  {loan.remainingInstallments}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Installment Amount
                                </Typography>
                                <Typography variant="h6">
                                  {formatCurrencyExact(loan.installmentAmount)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Total to Collect
                                </Typography>
                                <Typography variant="h6" color="info.main">
                                  {formatCurrencyExact(loan.totalToCollect)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Net Profit per Loan
                                </Typography>
                                <Typography variant="h6" color="success.main">
                                  {formatCurrencyExact(loan.netProfitPerLoan)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box
                                  sx={{
                                    p: 2,
                                    bgcolor: 'success.light',
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: 'success.main',
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Profit per Installment (FIXED)
                                  </Typography>
                                  <Typography variant="h4" color="success.dark" sx={{ fontWeight: 700 }}>
                                    {formatCurrencyExact(loan.profitPerInstallment)}
                                  </Typography>
                                  <Divider sx={{ my: 1.5 }} />
                                  <Typography variant="body2" color="success.dark" sx={{ fontWeight: 600 }}>
                                    Profit from Paid Installments:
                                  </Typography>
                                  <Typography variant="h6" color="success.dark" sx={{ fontWeight: 700 }}>
                                    {loan.paidCount || 0} × {formatCurrencyExact(loan.profitPerInstallment)} = {formatCurrencyExact(profitFromThisLoan)}
                                  </Typography>
                                  {interestFromThisLoan > 0 && (
                                    <>
                                      <Divider sx={{ my: 1.5 }} />
                                      <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 600 }}>
                                        Late Interest from Paid Installments:
                                      </Typography>
                                      <Typography variant="h6" color="warning.dark" sx={{ fontWeight: 700 }}>
                                        {formatCurrencyExact(interestFromThisLoan)}
                                      </Typography>
                                    </>
                                  )}
                                  <Divider sx={{ my: 1.5 }} />
                                  <Typography variant="body2" color="success.dark" sx={{ fontWeight: 600 }}>
                                    Total Profit (Installment + Interest):
                                  </Typography>
                                  <Typography variant="h5" color="success.dark" sx={{ fontWeight: 700 }}>
                                    {formatCurrencyExact(totalProfitFromThisLoan)}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    No profit data available for the selected date range.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="error">
                Failed to load profit details.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfitDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Due Installment Details Dialog */}
      <Dialog
        open={dueDialogOpen}
        onClose={() => setDueDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0D47A1' }}>
                Installment Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedInstallment?.customerName} - Loan #{selectedInstallment?.loanId}
              </Typography>
            </Box>
            <IconButton onClick={() => setDueDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInstallment && (
            <Box>
              {/* Basic Information */}
              <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Installment Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      #{selectedInstallment.installmentNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {new Date(selectedInstallment.dueDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Base Amount
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(selectedInstallment.baseAmount || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedInstallment.status}
                      size="small"
                      color={
                        selectedInstallment.status === 'PAID' ? 'success' :
                        selectedInstallment.status === 'OVERDUE' || selectedInstallment.status === 'LATE' ? 'error' :
                        selectedInstallment.status === 'PARTIAL' ? 'warning' : 'default'
                      }
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Total Payable Breakdown */}
              {selectedBreakdown && (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    p: 2,
                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(13, 71, 161, 0.05) 100%)',
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Total Payable Breakdown
                  </Typography>
                  
                  {selectedBreakdown.overdueInstallments > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Overdue Installments ({selectedBreakdown.overdueInstallments})
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {formatCurrency(selectedBreakdown.totalOverdueBase || 0)}
                      </Typography>
                      
                      {selectedBreakdown.overdueDetails && selectedBreakdown.overdueDetails.length > 0 && (
                        <Box sx={{ mt: 1, pl: 2 }}>
                          {selectedBreakdown.overdueDetails.map((detail, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                              Installment #{detail.installmentNumber} ({new Date(detail.dueDate).toLocaleDateString()}): {formatCurrency(detail.baseOutstanding || detail.baseAmount || 0)}
                              {detail.lateInterest > 0 && (
                                <Chip
                                  label={`Interest: ${formatCurrency(detail.lateInterest)}`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {selectedBreakdown.totalLateInterest > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Late Interest ({selectedBreakdown.interestRate}%)
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {formatCurrency(selectedBreakdown.totalLateInterest || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Calculated on {selectedBreakdown.overdueInstallments} overdue installment(s)
                      </Typography>
                    </Box>
                  )}

                  {selectedBreakdown.currentDayInstallment && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Day Installment
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatCurrency(selectedBreakdown.totalCurrentDayAmount || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Installment #{selectedBreakdown.currentDayInstallment.installmentNumber}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Payable
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D47A1' }}>
                      {formatCurrency(selectedBreakdown.totalPayable || 0)}
                    </Typography>
                  </Box>
                </Card>
              )}

              {/* Payment Information */}
              {selectedInstallment.paidAmount > 0 && (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Payment Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Paid Amount
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatCurrency(selectedInstallment.paidAmount || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Remaining Balance
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {formatCurrency(selectedInstallment.amountDue || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDueDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardHome;
